import { createReducer } from 'redux-act';
import { omit } from 'ramda';
import { TLegacyErrors } from '@alycecom/services';
import { StateStatus } from '@alycecom/utils';

import { IVerifiedAddress } from '../../types/Shipping.types';

import {
  fetchShippingAddress,
  resetError,
  resetErrors,
  resetShipping,
  resetVerification,
  setManualAddressMode,
  validateShipping,
} from './shipping.actions';
import { IShippingAddress, IShippingValidation } from './shipping.types';

export interface IShippingState {
  status: StateStatus;
  isValidated: boolean;
  address: IShippingValidation | null;
  verifiedAddress: IVerifiedAddress | null;
  isAddressVerified: boolean;
  isAddressChanged: boolean;
  isManualAddressMode: boolean;
  errors: TLegacyErrors<IShippingAddress>;
}

export const initialState: IShippingState = {
  status: StateStatus.Idle,
  isValidated: false,
  address: null,
  verifiedAddress: null,
  isAddressVerified: false,
  isAddressChanged: false,
  isManualAddressMode: false,
  errors: {},
};

export const shipping = createReducer<IShippingState>({}, initialState);

shipping
  .on(validateShipping.pending, state => ({
    ...state,
    status: StateStatus.Pending,
  }))
  .on(validateShipping.fulfilled, (state, { address, verifiedAddress, isAddressVerified, isAddressChanged }) => ({
    ...state,
    status: StateStatus.Fulfilled,
    isValidated: true,
    address: { address },
    verifiedAddress: verifiedAddress
      ? {
          ...verifiedAddress,
          countryId: address.country,
        }
      : null,
    isAddressVerified,
    isAddressChanged,
  }))
  .on(validateShipping.rejected, (state, payload) => ({
    ...state,
    status: StateStatus.Rejected,
    errors: payload,
  }));

shipping
  .on(resetError, (state, payload) => ({
    ...state,
    errors: omit([payload.name], state.errors),
  }))
  .on(resetErrors, state => ({
    ...state,
    errors: {},
  }))
  .on(resetShipping, state => ({
    ...state,
    isValidated: false,
    address: null,
    verifiedAddress: null,
    isAddressVerified: false,
    isAddressChanged: false,
    errors: {},
  }));

shipping.on(setManualAddressMode, (state, mode) => ({
  ...state,
  isManualAddressMode: mode,
}));

shipping.on(resetVerification, state => ({
  ...state,
  isValidated: false,
  verifiedAddress: null,
  isAddressVerified: false,
  isAddressChanged: false,
}));

shipping
  .on(fetchShippingAddress.pending, state => ({
    ...state,
    status: StateStatus.Pending,
  }))
  .on(fetchShippingAddress.fulfilled, (state, payload) => ({
    ...state,
    status: StateStatus.Fulfilled,
    address: payload
      ? {
          ...state.address,
          address: payload,
        }
      : null,
    isManualAddressMode: !!payload,
  }))
  .on(fetchShippingAddress.rejected, state => ({
    ...state,
    status: StateStatus.Rejected,
  }));
